import {
    IDocument,
    IDocumentCreate,
    IDocumentSearchParams,
    IPagedResult,
} from '@pf/shared-common';
import { Operation } from 'fast-json-patch';
import { Observable, of, switchMap, tap, throwError } from 'rxjs';

import { LoggerService } from '../logging/Logger.service';
import { DocumentsStore } from './DocumentsStore';
import { HashObject } from '@pf/shared-utility';

export interface DocumentsCrudMethods {
    get$(
        entityId: string,
        documentId: string,
        parentId?: string
    ): Observable<IDocument>;

    search$(
        entityId: string,
        params: IDocumentSearchParams,
        parentId?: string
    ): Observable<IPagedResult<IDocument>>;

    create$(
        entityId: string,
        document: IDocumentCreate,
        parentId?: string
    ): Observable<IDocument>;

    delete$(
        entityId: string,
        documentId: string,
        parentId?: string
    ): Observable<IDocument>;

    download$(
        entityId: string,
        documentId: string,
        parentId?: string
    ): Observable<Blob>;

    patch$?: (
        entityId: string,
        documentId: string,
        operations: readonly Operation[],
        parentId?: string
    ) => Observable<IDocument>;
}

export class DocumentsDataService {
    constructor(
        private entityName: string,
        private crudMethods: DocumentsCrudMethods,
        private store: DocumentsStore,
        private _logger: LoggerService
    ) {}

    get$(entityId: string, documentId: string): Observable<IDocument> {
        if (this.store.exists(documentId)) {
            return of(this.store.get(documentId));
        }
        this._logger.debug(`Fetching ${this.entityName}`);
        return this.crudMethods
            .get$(entityId, documentId)
            .pipe(tap(entity => this.store.add(entity, false)));
    }

    search$(
        entityId: string,
        queryParams: IDocumentSearchParams,
        parentId?: string
    ): Observable<IPagedResult<IDocument>> {
        this._logger.debug(`Searching ${this.entityName}`);
        const pageHash = HashObject({
            entityId,
            ...queryParams,
            parentId,
        });
        return this.crudMethods.search$(entityId, queryParams, parentId).pipe(
            tap(result => this.store.setPage(result, pageHash)),
            this.store.skipWhilePageExists(
                queryParams.pageNumber as number,
                pageHash
            )
        );
    }

    create$(
        entityId: string,
        body: IDocumentCreate,
        parentId?: string
    ): Observable<IDocument> {
        this._logger.debug(`Creating ${this.entityName}`);
        return this.crudMethods
            .create$(entityId, body, parentId)
            .pipe(tap(created => this.store.add(created, true)));
    }

    update$(
        entityId: string,
        documentId: string,
        update: IDocumentCreate,
        parentId?: string
    ): Observable<IDocument> {
        this._logger.debug(
            `Updating document ${documentId} for ${this.entityName}: ${entityId}`
        );
        return this.crudMethods
            .delete$(entityId, documentId, parentId)
            .pipe(switchMap(() => this.crudMethods.create$(entityId, update)));
    }

    delete$(
        entityId: string,
        documentId: string,
        parentId?: string
    ): Observable<IDocument> {
        this._logger.debug(
            `Deleting document ${documentId} for ${this.entityName}: ${entityId}`
        );
        return this.crudMethods
            .delete$(entityId, documentId, parentId)
            .pipe(tap(this.store.delete.bind(this.store)));
    }

    patch$(
        entityId: string,
        documentId: string,
        operations: readonly Operation[],
        parentId?: string
    ): Observable<IDocument> {
        const patch = this.crudMethods.patch$;
        if (!patch) {
            return throwError(
                () =>
                    new Error(
                        `Document JSON Patch is not implemented for "${this.entityName}". ` +
                            'Only document adapters that register patch$ on DocumentsCrudMethods support this operation.'
                    )
            );
        }
        this._logger.debug(
            `Patching document ${documentId} for ${this.entityName}: ${entityId}`
        );
        return patch(entityId, documentId, operations, parentId).pipe(
            tap(patched => this.store.update(patched))
        );
    }

    download$(
        entityId: string,
        documentId: string,
        parentId?: string
    ): Observable<Blob> {
        this._logger.debug(
            `Downloading document ${documentId} for ${this.entityName}: ${entityId}`
        );
        return this.crudMethods.download$(entityId, documentId, parentId);
    }
}
